import { Injectable } from '@angular/core';

export interface TrackingData {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  fbclid?: string;
  fbc?: string;
  fbp?: string;

  originalUrl?: string;
}

@Injectable({ providedIn: 'root' })
export class TrackingCookieService {
  private readonly COOKIE_NAME = '_jt_tracking';
  private readonly EXPIRY_DAYS = 30;

  captureFromUrl(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const data: TrackingData = this.getTrackingData();

    const utmSource = params.get('utm_source');
    if (utmSource) data.utmSource = utmSource;

    const utmMedium = params.get('utm_medium');
    if (utmMedium) data.utmMedium = utmMedium;

    const utmCampaign = params.get('utm_campaign');
    if (utmCampaign) data.utmCampaign = utmCampaign;

    const utmContent = params.get('utm_content');
    if (utmContent) data.utmContent = utmContent;

    const utmTerm = params.get('utm_term');
    if (utmTerm) data.utmTerm = utmTerm;

    if (utmSource || utmMedium || utmCampaign || utmContent || utmTerm) {
      data.originalUrl = window.location.href;
    }

    const fbclid = params.get('fbclid');
    if (fbclid) {
      data.fbclid = fbclid;

      data.fbc = `fb.1.${Date.now()}.${fbclid}`;
    }

    // Try to read FB cookies right away (may not exist yet)
    const fbp = this.getCookie('_fbp');
    if (fbp) data.fbp = fbp;

    const existingFbc = this.getCookie('_fbc');
    if (existingFbc) data.fbc = existingFbc;

    // Persist immediately with whatever we have
    this.saveToCookie(data);

    // Start async polling for the real FB cookies (pixel loads asynchronously)
    this.pollForFbCookies(data);
  }

  /**
   * Returns the current tracking data from the cookie.
   */
  getTrackingData(): TrackingData {
    const raw = this.getCookie(this.COOKIE_NAME);
    if (!raw) return {};
    try {
      return JSON.parse(decodeURIComponent(raw));
    } catch {
      return {};
    }
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /**
   * Polls for _fbp (and _fbc) cookies that Facebook Pixel sets asynchronously.
   * If found within the retry window, updates the stored tracking cookie.
   */
  private async pollForFbCookies(
    data: TrackingData,
    maxRetries = 10,
    delayMs = 500,
  ): Promise<void> {
    for (let i = 0; i < maxRetries; i++) {
      const fbp = this.getCookie('_fbp');
      const fbc = this.getCookie('_fbc');

      if (fbp) {
        data.fbp = fbp;
        if (fbc) data.fbc = fbc;
        this.saveToCookie(data);
        return;
      }

      await this.delay(delayMs);
    }

    // Final attempt after all retries
    const fbp = this.getCookie('_fbp');
    const fbc = this.getCookie('_fbc');
    if (fbp) data.fbp = fbp;
    if (fbc) data.fbc = fbc;
    this.saveToCookie(data);
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private saveToCookie(data: TrackingData): void {
    const expires = new Date();
    expires.setDate(expires.getDate() + this.EXPIRY_DAYS);
    const value = encodeURIComponent(JSON.stringify(data));
    document.cookie = `${this.COOKIE_NAME}=${value}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
  }

  private getCookie(name: string): string | null {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(
      new RegExp(`(?:^|;\\s*)${name}=([^;]*)`),
    );
    return match ? decodeURIComponent(match[1]) : null;
  }
}
